import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConnectionStore } from "../stores/connection";
import { useAuthStore } from "../stores/auth";
import { Loader2, ShieldAlert, ShieldX } from "lucide-react";
import { StatusDialog } from "./StatusDialog";

const DISCONNECT_GRACE_MS = 3000;

export function ConnectionStatusOverlay() {
  const status = useConnectionStore(s => s.status);
  const disconnectReason = useConnectionStore(s => s.disconnectReason);
  const limitedMode = useConnectionStore(s => s.limitedMode);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const logout = useAuthStore(s => s.logout);
  const navigate = useNavigate();
  const [showTransient, setShowTransient] = useState(false);

  const isPermanent = status === "disconnected_permanent" && !!disconnectReason;
  const isTransientDisconnect = status === "disconnected" && !disconnectReason && isAuthenticated;

  useEffect(() => {
    if (!isTransientDisconnect) {
      setShowTransient(false);
      return;
    }
    const timer = setTimeout(() => setShowTransient(true), DISCONNECT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isTransientDisconnect]);

  const goToLogin = () => {
    logout();
    navigate("/login");
  };

  if (limitedMode) {
    return (
      <StatusDialog
        icon={ShieldX}
        iconClassName="text-warning"
        mediaClassName="bg-warning/10"
        title="Account Suspended"
        description="Your account has been disabled by an administrator. Contact an administrator for assistance."
        actionLabel="Go to Login"
        onAction={goToLogin}
      />
    );
  }

  if (isPermanent) {
    const isDeleted = disconnectReason === "account_deleted";
    return (
      <StatusDialog
        icon={ShieldAlert}
        iconClassName={isDeleted ? "text-destructive" : "text-warning"}
        mediaClassName={isDeleted ? "bg-destructive/10" : "bg-warning/10"}
        title={isDeleted ? "Account Removed" : "Account Suspended"}
        description={
          isDeleted
            ? "Your account has been removed. Please contact an administrator if you believe this is an error."
            : "Your account has been suspended. Please contact an administrator to restore access."
        }
        actionLabel="Go to Login"
        onAction={goToLogin}
      />
    );
  }

  if (!showTransient) return null;

  return (
    <StatusDialog
      icon={Loader2}
      iconClassName="text-muted-foreground animate-spin"
      mediaClassName="bg-muted"
      title="Connection Lost"
      description="Attempting to reconnect to the server..."
    />
  );
}
